/* eslint-disable no-param-reassign */
import { Module } from 'vuex'
import { RootState } from 'surface/xstore/types'
import PadletApi from 'surface/padlet_api'
import { Id } from 'types'
import { captureException } from 'bits/error_tracker'
interface Reaction {
  id: number
  reaction_type: string
  updated_at: Date | string
  user_id: any
  value: number
  wish_id: Id
}
type ReactionId = string | number

interface ReactionState {
  reactionsFetched: boolean
  reactionEntities: { [id: string]: any }
  pendingReactionIds: ReactionId[]
  postIdBeingReactedTo: Id | null
  reactionDropdownTop: number | null
  reactionDropdownLeft: number | null
}

const ReactionModule: Module< ReactionState, RootState> = {
  namespaced: true,
  state: {
    reactionsFetched: false,
    reactionEntities: {},
    pendingReactionIds: [],
    postIdBeingReactedTo: null,
    reactionDropdownTop: null,
    reactionDropdownLeft: null,
  },
  getters: {
    reactions (state): Reaction[] {
      return Object.values(state.reactionEntities)
    },
    reactionIds (state): string[] {
      return Object.keys(state.reactionEntities)
    },
    reactionIdsByPost (state, getters, rootState, rootGetters): { [id: number]: ReactionId[] } {
      const reactionsByPost = {}
      getters.reactionIds.forEach((rid: ReactionId): void => {
        const r = state.reactionEntities[rid]
        reactionsByPost[r.wish_id] = reactionsByPost[r.wish_id] || []
        if (r.reaction_type === rootGetters.reactionData.type) {
          reactionsByPost[r.wish_id].push(rid)
        }
      })
      return reactionsByPost
    },
    hasReactions (state, getters): boolean {
      return getters.reactionIds.length > 0
    },
    maxGrade (state, getters, rootState, rootGetters): number {
      if (!rootGetters.isReactable) return 0
      // Value might be blank when user is previewing settings
      const maxValue = rootGetters.reactionData.max_value || 0
      // Avoid out-of-memory due to too many <option>s
      return Math.min(maxValue, 100)
    },
    myReaction (state, getters, rootState): Reaction {
      const myReactions = getters.reactions.filter((reaction: Reaction): boolean => (
        reaction.user_id === rootState.user.id &&
        reaction.wish_id === state.postIdBeingReactedTo
      ))
      return (myReactions.length > 0) ? myReactions[0] : null
    },
    xReactionDropdownStar (state, getters, rootState, rootGetters): boolean {
      return !!state.postIdBeingReactedTo && rootGetters.reactionData.type === 'star'
    },
    xReactionDropdownGrade (state, getters, rootState, rootGetters): boolean {
      return !!state.postIdBeingReactedTo && rootGetters.reactionData.type === 'grade'
    },
    postIdBeingReactedTo: (state): Id|null => state.postIdBeingReactedTo,
  },
  mutations: {
    FETCH_REACTIONS_SUCCESS (state, reactions): void {
      if (reactions) {
        const newReactionEntities = {}
        reactions.forEach((r: Reaction): void => {
          newReactionEntities[r.id] = r
        })
        state.reactionEntities = newReactionEntities // Trigger state change once
        state.pendingReactionIds = []
      }
      state.reactionsFetched = true
    },
    UPDATE_REACTION (state, { id, obj }): void {
      if (id < 0 || !obj) {
        return
      }
      const updatedEntity = {
        ...state.reactionEntities[id],
        ...obj,
      }
      state.reactionEntities = {
        ...state.reactionEntities,
        [id]: updatedEntity,
      }
    },
    ADD_PENDING_REACTION (state, pendingReaction): void {
      const isPendingReactionAlreadyThere = state.pendingReactionIds.some((id: ReactionId): boolean => {
        if ((state.reactionEntities[id].wish_id === pendingReaction.wish_id) &&
          (state.reactionEntities[id].value === pendingReaction.value)) {
          return true
        }
        return false
      })
      if (!isPendingReactionAlreadyThere) {
        const tempId = 'new_' + new Date().valueOf()
        state.reactionEntities = {
          ...state.reactionEntities,
          [tempId]: pendingReaction,
        }
        state.pendingReactionIds.push(tempId)
      }
    },
    ADD_REACTION (state, reaction): void {
      const pendingReactionIdRemoved = state.pendingReactionIds.some((id: ReactionId): boolean => {
        if ((state.reactionEntities[id].wish_id === reaction.wish_id) &&
        (state.reactionEntities[id].value === reaction.value) &&
        (state.reactionEntities[id].user_id === reaction.user_id) &&
        (state.reactionEntities[id].reaction_type === reaction.reaction_type)) {
          const newReactionEntities = {
            ...state.reactionEntities,
          }
          delete newReactionEntities[id]
          state.reactionEntities = {
            ...newReactionEntities,
            [reaction.id]: reaction,
          }
          state.pendingReactionIds = state.pendingReactionIds.filter((i: ReactionId): boolean => i !== id)
          return true
        }
        return false
      })
      if (!pendingReactionIdRemoved) {
        state.reactionEntities = {
          ...state.reactionEntities,
          [reaction.id]: reaction,
        }
      }
    },
    REMOVE_REACTION (state, id): void {
      const newReactionEntities = { ...state.reactionEntities }
      delete newReactionEntities[id]
      state.reactionEntities = newReactionEntities
    },
    EDIT_REACTION_REMOTE (state, reaction): void {
      const originalReaction = state.reactionEntities[reaction.id]
      // real time can be out of sync sometimes. here we make sure we are not updating a new copy with a stale one
      if (originalReaction && originalReaction.updated_at <= reaction.updated_at) {
        state.reactionEntities = {
          ...state.reactionEntities,
          [reaction.id]: reaction,
        }
      }
    },
    START_EDITING_REACTION (state, { postId, dropdownTop, dropdownLeft }): void {
      state.postIdBeingReactedTo = postId
      state.reactionDropdownTop = dropdownTop
      state.reactionDropdownLeft = dropdownLeft
    },
    STOP_EDITING_REACTION (state): void {
      state.postIdBeingReactedTo = null
    },
  },
  actions: {
    fetchReactions ({ dispatch, rootState }): void {
      PadletApi.Reaction.readAll({ wall: { id: rootState.wall.id } })
        .then((reactions: Reaction): void => {
          dispatch('fetchReactionsSuccess', { reactions })
        }).catch(captureException)
    },
    fetchReactionsSuccess ({ commit }, { reactions }): void {
      commit('FETCH_REACTIONS_SUCCESS', reactions)
    },
    createReaction ({
      commit, state, getters, rootState, rootGetters, dispatch,
    }, { postId, value }): void {
      const existingReaction = getters.reactions.find((reaction: Reaction): boolean => (
        reaction.user_id === rootState.user.id &&
        reaction.wish_id === postId &&
        reaction.reaction_type === rootGetters.reactionData.type
      ))
      if (existingReaction) {
        commit('UPDATE_REACTION', {
          id: existingReaction.id,
          obj: {
            value: value,
          },
        })
        PadletApi.Reaction.update({ ...state.reactionEntities[existingReaction.id], value })
      } else {
        const newReaction = {
          wish_id: postId,
          value: value,
          reaction_type: rootGetters.reactionData.type,
        }
        // not handling pending reaction for now, request is quick enough
        commit('ADD_PENDING_REACTION', {
          ...newReaction,
          user_id: rootState.user.id,
        })
        PadletApi.Reaction.create(newReaction)
          .then((reaction: Reaction): void => {
            dispatch('createReactionSuccess', { reaction })
          })
      }
    },
    createReactionSuccess ({ commit }, { reaction }): void {
      commit('ADD_REACTION', reaction)
    },
    deleteReaction ({ commit, getters, rootState }, { postId }): void {
      const existingReaction = getters.reactions.find((reaction: Reaction): boolean => (
        reaction.user_id === rootState.user.id &&
        reaction.wish_id === postId
      ))
      commit('STOP_EDITING_REACTION')
      if (existingReaction) {
        commit('REMOVE_REACTION', existingReaction.id)
        PadletApi.Reaction.delete(existingReaction)
      }
    },
    startEditingReaction ({ commit }, payload): void {
      commit('START_EDITING_REACTION', payload)
    },
    stopEditingReaction ({ commit }): void {
      commit('STOP_EDITING_REACTION')
    },
    newReactionRemote ({ commit }, payload): void {
      commit('ADD_REACTION', payload)
    },
    editReactionRemote ({ commit }, payload): void {
      commit('EDIT_REACTION_REMOTE', payload)
    },
    deleteReactionRemote ({ commit, state }, reaction): void {
      if (state.postIdBeingReactedTo === reaction.wish_id) {
        commit('STOP_EDITING_REACTION')
      }
      commit('REMOVE_REACTION', reaction.id)
    },
  },
}

export default ReactionModule
export {
  Reaction,
  ReactionState,
}
